import { useEffect, useState, type KeyboardEvent } from 'react';
import { NAME_MAX_LENGTH } from '@cavaquinho/shared';
import { Avatar } from '../../ui/primitives/Avatar.tsx';
import { Field } from '../../ui/primitives/Field.tsx';
import { Input } from '../../ui/primitives/Input.tsx';
import { Text } from '../../ui/primitives/Text.tsx';
import styles from './account.module.css';

/**
 * Who this page is about — and the one thing about them that is theirs to
 * rewrite.
 *
 * THE NAME IS A FIELD, NOT A ROW WITH AN "EDITAR" BUTTON. A button that turns
 * a label into a text box is a step in front of a text box; the field is
 * already the control, so it is simply rendered as one. It saves when the
 * field is left and when `Enter` is pressed, `Escape` puts back what was
 * there, and nothing is saved unless the trimmed value actually changed —
 * clicking in and out of it costs no request and no announcement.
 *
 * THE PICTURE IS NOT EDITABLE HERE, and that is a decision rather than an
 * omission: it comes from the Google account this one signs in with, and
 * where there is none `Avatar` draws the initials it draws everywhere else.
 * One source for a face, and nothing to keep in step with it.
 *
 * The email is stated and not offered: it is the identity the session is
 * built on, and changing it is changing which Google account this is.
 */

interface AccountIdentityProps {
  name: string;
  email: string | null;
  image: string | null;
  /** The save is in flight. */
  busy: boolean;
  /** A failure from the server, already in the learner's language. */
  error: string | null;
  /** Shown for a moment after a successful save (§ 12 also announces it). */
  saved: boolean;
  onSave: (name: string) => void;
}

export function AccountIdentity({
  name,
  email,
  image,
  busy,
  error,
  saved,
  onSave,
}: AccountIdentityProps) {
  const [draft, setDraft] = useState(name);
  const [tooLong, setTooLong] = useState(false);

  /*
   * The account is re-read after every write (`AuthProvider.refresh`), so the
   * saved name arrives here as a new prop. Following it keeps the field
   * honest about what is stored — including when the server trimmed what was
   * typed — without the field fighting the person while they type: only a
   * name that CHANGED upstream is adopted.
   */
  useEffect(() => {
    setDraft(name);
    setTooLong(false);
  }, [name]);

  function commit() {
    const next = draft.trim();
    if (next === name) {
      setDraft(name);
      setTooLong(false);
      return;
    }
    if (next.length === 0) {
      // An empty name is not a request to be nameless; it is a field someone
      // cleared and walked away from. Put the name back.
      setDraft(name);
      setTooLong(false);
      return;
    }
    if (next.length > NAME_MAX_LENGTH) {
      setTooLong(true);
      return;
    }
    setTooLong(false);
    onSave(next);
  }

  function onKeyDown(event: KeyboardEvent<HTMLElement>) {
    if (event.key === 'Enter') {
      event.preventDefault();
      commit();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      setDraft(name);
      setTooLong(false);
    }
  }

  return (
    <header className={styles.rail} data-testid="account-identity">
      <Text as="h1" variant="heading">
        Sua conta
      </Text>

      <Avatar name={name} image={image} size="lg" />

      <div className={styles.nameField} onKeyDown={onKeyDown} onBlur={commit}>
        <Field
          size="compact"
          label="Nome"
          htmlFor="account-name"
          error={tooLong ? `Um nome vai até ${NAME_MAX_LENGTH} caracteres.` : (error ?? undefined)}
        >
          <Input
            id="account-name"
            value={draft}
            onChange={setDraft}
            size="compact"
            disabled={busy}
            invalid={tooLong || error !== null}
          />
        </Field>

        {/*
          The field's own feedback line, held for both states so the layout
          does not jump as one replaces the other. `LiveRegion` on the page
          carries the same words to anyone not looking at it.
        */}
        <p className={styles.nameStatus} aria-hidden="true">
          <Text as="span" variant="caption" tone={saved ? 'success' : 'muted'}>
            {busy ? 'Salvando…' : saved ? 'Salvo' : 'Aparece no ranking e para quem segue você.'}
          </Text>
        </p>
      </div>

      {email ? (
        <Text as="p" variant="body-sm" tone="muted">
          {email}
        </Text>
      ) : null}
    </header>
  );
}
